import { memo, useEffect, useState } from "react";
import BackButton from "./components/BackButton";
import { useParams } from "react-router-dom";
import type { PackageTypeRouteT } from "../../models/models";
import AddonPriceDetails from "./components/AddonPriceDetails";
import SubscriptionPriceDetails from "./components/SubscriptionPriceDetails";
import { useContent } from "../../../../../contexts/contentContext/contentContext";
import usePurchases from "../../hooks/usePurchases";
import { PurchaseStepE } from "../../models/enums";
import useCreditCard from "../../hooks/useCreditCard";
import Button from "../../../../Elements/Button/Button";
import parse from "html-react-parser";
import useTransaction from "../../hooks/useTransaction";
import useDowngrade from "../../hooks/useDowngrade";
import useAddon from "../../hooks/useAddon";
import LoadingImageElement from "../../../../Elements/LoadingContainerElement/LoadingImageElement";
import { LinkToNewTab } from "../../../../Elements/Link/Link";
import Image from "../../../../Elements/Image/Image";

import styles from "./styles.module.scss";

const PurchasePaymentDetails = memo(() => {
  const { packageType } = useParams<{
    packageType: PackageTypeRouteT;
  }>();
  const { getTextByKey } = useContent();
  const { setCompletedStep, isFetchingPurchasePrice } = usePurchases();
  const { paymentType } = useCreditCard();
  const { createTransaction, isTransactionExecuting, newCreditCardIframeUrl } =
    useTransaction();
  const { addonQuantity, existingAddonQuantity } = useAddon();
  const {
    downgradeAddon,
    downgradeSubscription,
    validateDowngradeSubscriptions,
    isDowngrade,
    downgradeAddonErrorMessage,
    clearDowngradeAddonErrorMessage,
  } = useDowngrade();
  const [isLoading, setIsLoading] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  // Scroll to top every time the component is rendered
  useEffect(() => {
    window.scrollTo(0, 0);

    return () => {
      clearDowngradeAddonErrorMessage();
    };
  }, []);

  const completePurchase = () => {
    if (!isTermsAccepted) {
      setShowTermsError(true);
      return;
    }
    setShowTermsError(false);
    setCompletedStep(PurchaseStepE.THIRD_PAYMENT_STEP);

    let allowContinue = true;
    if (
      packageType === "business" ||
      packageType === "premium" ||
      packageType === "pro"
    ) {
      allowContinue = validateDowngradeSubscriptions();
    }
    if (allowContinue) {
      setIsLoading(true);

      const isDowngradeAddon =
        (packageType === "sub-users" || packageType === "extra-storage") &&
        existingAddonQuantity > addonQuantity;
      if (!isDowngrade && !isDowngradeAddon) {
        createTransaction(false);
      } else if (isDowngrade && !isDowngradeAddon) {
        // downgradeSubscription();
        createTransaction(true);
      } else if (isDowngradeAddon) {
        downgradeAddon();
      }
    }
  };

  return (
    <>
      <BackButton />
      <div
        className={`${styles.wrapper}
          ${packageType === "pay-as-you-go" ? styles.codesHide : ""}`}
      >
        {isTransactionExecuting ? (
          <div className={styles.loadingWrapper}>
            <LoadingImageElement />
          </div>
        ) : (
          <>
            <div>
              <h5>{getTextByKey("chosen-purchase")}</h5>
            </div>
            {packageType === "sub-users" || packageType === "extra-storage" ? (
              <AddonPriceDetails />
            ) : (
              <SubscriptionPriceDetails />
            )}
            <div className={styles.disclaimerText}>
              {parse(getTextByKey("disclaimer-text"))}
            </div>
            <label
              className={`${styles.termsCheckbox} ${
                showTermsError ? styles.error : ""
              }`}
            >
              <input
                type="checkbox"
                id="terms-checkbox"
                checked={isTermsAccepted}
                onChange={(e) => {
                  setIsTermsAccepted(e.target.checked);
                  if (e.target.checked) {
                    setShowTermsError(false);
                  }
                }}
              />
              <Image
                name={
                  isTermsAccepted ? "checkbox-checked" : "checkbox-unchecked"
                }
              />
              <div className={styles.inputLabel}>
                {parse(getTextByKey("agree-with-terms"))}
              </div>
            </label>
            <div className={styles.continuePurchaseButton}>
              {downgradeAddonErrorMessage === "sub-users-license-count" ? (
                //@ts-ignore
                <LinkToNewTab page="user/profile/account-users ">
                  <Button className={styles.newButton}>
                    {getTextByKey("manage-users-button")}
                  </Button>
                </LinkToNewTab>
              ) : (
                <Button
                  className={styles.newButton}
                  onClick={completePurchase}
                  disabled={
                    isLoading ||
                    newCreditCardIframeUrl ||
                    isFetchingPurchasePrice
                  }
                >
                  {(isLoading && !newCreditCardIframeUrl) ||
                  isFetchingPurchasePrice ? (
                    <LoadingImageElement />
                  ) : (
                    getTextByKey(
                      paymentType !== "paypal"
                        ? "continue-payment"
                        : "continue-to-paypal"
                    )
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
});

export default PurchasePaymentDetails;
